import styled, { css } from 'styled-components';

import { AddPhotoTheme, Color, FontWeight } from '../../enums';
import { StyledComponent } from '../../interfaces';
import { Image } from '../image/image.component';
import { ValidationBar } from '../validationBar/validationBar.component';

import { AddPhotoBarProps, FileRowProps, PhotoPreviewContainerProps, ProgressBarProps, SmallContainerProps } from './addPhoto.types';

export const Container: StyledComponent<'div'> = styled.div``;

export const AddPhotoBox: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid ${Color.Gray300};
  height: 222px;
  width: 100%;
  position: relative;
`;

export const AddPhotoIconContainer: StyledComponent<'div'> = styled.div`
  width: 44px;
  height: 44px;
  background-color: ${Color.Gray200};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

export const PhotoBoxHeader: StyledComponent<'div'> = styled.div`
  font-weight: ${FontWeight.Medium};
  font-size: 18px;
  line-height: 26px;
`;

export const PhotoBoxSubheader: StyledComponent<'div'> = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${Color.Gray500};
`;

export const PhotoPreviewContainer: StyledComponent<'div', PhotoPreviewContainerProps> = styled.div<PhotoPreviewContainerProps>`
  width: 100%;
  background: ${Color.Gray500};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: 0.3s;
  height: ${({ $visible }: PhotoPreviewContainerProps): string => $visible ? '440px' : '0'};
`;

export const PhotoPreview: StyledComponent<typeof Image> = styled(Image)`
  max-width: 100%;
  max-height: 100%;
`;

export const SizeErrorMessageContainer: StyledComponent<'div'> = styled.div``;

export const SizeErrorMessage: StyledComponent<'div'> = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${Color.White};
  background-color: ${Color.Error500};
  padding: 5px;
`;

export const ArrowsContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: 24px;
  opacity: 0;
  transition: .3s;
`;

export const FilesList: StyledComponent<'div'> = styled.div`
  margin-top: 12px;
`;

export const FileRowContainer: StyledComponent<'div'> = styled.div``;

export const FileRow: StyledComponent<'div', FileRowProps> = styled.div<FileRowProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  border-top: 1px solid ${Color.Gray300};
  width: 100%;
  background-color: ${({ $active }: FileRowProps): Color => $active ? Color.Error300 : Color.Gray100};
  transition: .3s;

  ${({ $active, $isFirstHover }: FileRowProps) => !$active ? css`
    &:first-of-type {
      background-color: ${$isFirstHover ? Color.Error100 : Color.Gray100};

      ${ArrowsContainer} {
        opacity: ${$isFirstHover ? '1' : '0'};
      }
    }
    
    &:hover {
      background-color: ${Color.Error100};

      ${ArrowsContainer} {
        opacity: 1;
      }
    }
  ` : css`
    ${ArrowsContainer} {
      opacity: 1;
    }
  `};

  &:last-of-type {
    border-bottom: 1px solid ${Color.Gray300};
  }
`;

export const ProgressBarContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
  background-color: ${Color.Transparent};
  padding: 0 20px;
  width: 100%;
`;

export const SmallProgressBarContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
`;

export const ProgressBar: StyledComponent<'div', ProgressBarProps> = styled.div<ProgressBarProps>`
  background-color: ${Color.Error500};
  height: 10px;
  width: ${({ $width }: ProgressBarProps): string => $width};
  border-radius: 50px;
`;

export const FileCell: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
  padding: 7px 12px;
  background: ${Color.White};
  border: 1px solid ${Color.Gray300};
  border-radius: 50px;
  flex-shrink: 0;
`;

export const FileLabel: StyledComponent<'div'> = styled.div`
  margin-left: 5px;
  font-size: 14px;
  line-height: 18px;
  color: ${Color.Gray500};
`;

export const FileRemoveButton: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  height: 100%;
`;

export const AddPhotoBar: StyledComponent<'button', AddPhotoBarProps> = styled.button.attrs({ type: 'button' })<AddPhotoBarProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Color.Gray100};
  padding: 17px 0;
  border-bottom: 1px solid ${Color.Gray300};
  width: 100%;
  max-width: ${({ $theme }: AddPhotoBarProps): string => $theme === AddPhotoTheme.SmallWithAvatar ? '408px' : '100%'};
  position: relative;
`;

export const AddPhotoBarLabel: StyledComponent<'div'> = styled.div`
  font-weight: ${FontWeight.Medium};
  font-size: 16px;
  line-height: 22px;
  color: ${Color.Gray500};
  margin-left: 10px;
`;

export const RequirementsBox: StyledComponent<'div'> = styled.div`
  margin-top: 24px;
`;

export const Requirement: StyledComponent<'div'> = styled.div`
  display: flex;

  & + & {
    margin-top: 8px;
  }
`;

export const Dot: StyledComponent<'div'> = styled.div`
  width: 3px;
  height: 3px;
  background-color: ${Color.Black};
  border-radius: 50%;
  margin-top: 9.5px;
  margin-right: 11px;
  flex-shrink: 0;
`;

export const RequirementLabel: StyledComponent<'div'> = styled.div`
  font-weight: ${FontWeight.Light};
  font-size: 16px;
  line-height: 22px;
  color: ${Color.Gray500};
`;

export const FileInput: StyledComponent<'input'> = styled.input`
  opacity: 0;
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
`;

export const RowButtonsContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
`;

export const ArrowButton: StyledComponent<'button'> = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: center;

  & + & {
    margin-top: 10px;
  }
`;

export const SmallAddPhoto: StyledComponent<'div'> = styled.div`
  width: 100%;
  height: 144px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AvatarContainer: StyledComponent<'div'> = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  border: 1px solid ${Color.Gray300};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const SmallContainer: StyledComponent<'div', SmallContainerProps> = styled.div<SmallContainerProps>`
  padding: 22px 10px 0;
  max-width: 408px;
  background-color: ${({ $withFile }: SmallContainerProps): Color => $withFile ? Color.Gray200 : Color.Transparent};
  border: 1px solid ${Color.Gray300};
  margin-bottom: 12px;
`;

export const AvatarImage: StyledComponent<typeof Image> = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const ButtonsContainer: StyledComponent<'div'> = styled.div`
  display: flex;
  align-items: center;
  max-width: 408px;

  ${AddPhotoBar} + ${AddPhotoBar} {
    margin-left: 12px;
  }

  ${AddPhotoBar} {
    background-color: ${Color.Gray200};
  }

  ${AddPhotoBarLabel} {
    margin-left: 0;
  }
`;

export const StyledValidationBar: StyledComponent<typeof ValidationBar> = styled(ValidationBar)`
  max-width: 408px;
  margin-top: 12px;
`;
